import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export const PredictionsTeaser = () => {
  const { lang } = useLanguage();
  return (
    <section
      className="relative py-20 lg:py-28 px-6 lg:px-12"
      data-testid="predictions-teaser"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative glass-dark border border-[#d4af37]/40 rounded-3xl p-8 lg:p-14 overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#d4af37]/8 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-[#5A0001]/20 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
                <Sparkles className="w-4 h-4" />
                <span className="uppercase tracking-[0.3em] text-xs">
                  {lang === "es" ? "Prueba Documentada" : "Documented Proof"}
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-white mb-4 leading-tight">
                {lang === "es" ? (
                  <>
                    Mis Predicciones <span className="gold-text italic">Se Cumplen</span>
                  </>
                ) : (
                  <>
                    My Predictions <span className="gold-text italic">Come True</span>
                  </>
                )}
              </h2>
              <p className="text-neutral-300 leading-relaxed mb-6">
                {lang === "es"
                  ? "Terremoto en Indonesia, inundaciones en Veracruz, eventos sobre el Líder Supremo de Irán... Llevo 27 predicciones cumplidas y documentadas; en esta página mostramos algunas, pero puedes ver todas en mi perfil de TikTok."
                  : "Earthquake in Indonesia, floods in Veracruz, events about the Supreme Leader of Iran... I have 27 fulfilled and documented predictions; this page shows a few of them, but you can see them all on my TikTok profile."}
              </p>

              <div className="space-y-2 mb-6">
                {[
                  lang === "es" ? "Terremoto en Indonesia" : "Earthquake in Indonesia",
                  lang === "es" ? "Inundaciones en Veracruz" : "Floods in Veracruz",
                  lang === "es" ? "El Líder Supremo de Irán" : "The Supreme Leader of Iran",
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-neutral-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {t}
                  </div>
                ))}
              </div>

              <Link
                to="/predicciones-cumplidas"
                data-testid="predictions-teaser-cta"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full uppercase text-xs tracking-[0.25em]"
              >
                {lang === "es" ? "Ver las Predicciones" : "See the Predictions"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Decorative stat block */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {[
                { num: "27", label: lang === "es" ? "Predicciones cumplidas" : "Fulfilled predictions" },
                { num: "100%", label: lang === "es" ? "Verificables" : "Verifiable" },
                { num: "TikTok", label: lang === "es" ? "Documentadas en" : "Documented on" },
                { num: "Live", label: lang === "es" ? "En tiempo real" : "Real time" },
              ].map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-black/40 p-4 text-center">
                  <div className="font-serif text-2xl lg:text-3xl gold-text mb-1">{s.num}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
